import { getRailsClient } from "../client";

// Verified from GET /api/internal_api/atlas/v1/current_user/enrollments
// Shape is structurally identical to ActivePath in LevelContextProvider.
export interface Enrollment {
  id: number;
  roleId: number;
  name: string;
  description: string;
  collectionKey: string | null;
  roleEnrollmentId: number;
  lessonsInEnrollment: number;
  lessonsPassed: number;
  estimatedTimeToComplete: number;
  completed: boolean;
  totalPoints: number;
  nextLesson: {
    id: number;
    lessonKey: string;
    name: string;
    description: string;
    estimatedTime: number;
    difficulty: string;
    lessonType: string;
    status: string;
    required: boolean;
  } | null;
  enrollmentType: string;
  levelRank: string | null;
}

// Verified from POST /api/internal_api/atlas/v1/current_user/role_enrollments
// (RoleEnrollmentsOperations::Enroll → { role, role_enrollment, user }). role_enrollment is
// the RoleEnrollment record, and is null until the enrollment is first created — so callers
// must null-check before reading id.
interface EnrollmentResponse {
  roleEnrollment: { id: number } | null;
}

// TODO: verify unenrollment response shape (endpoint has no active call sites)
type EnrollmentMutationResponse = Record<string, unknown>;

const UserEnrollmentsRoute = "/api/internal_api/atlas/v1/current_user/enrollments";

export const getEnrollments = async ({ useToast = true } = {}) => {
  return getRailsClient({ useToast })
    .get<{ currentEnrollments: Enrollment[] }>(UserEnrollmentsRoute)
    .then(async response => {
      if (response.status === 200) {
        return response.data.currentEnrollments;
      } else {
        return Promise.reject(
          new Error(`getEnrollments: Unable to get enrollments, status code ${response.status}`)
        );
      }
    });
};

const UserEnrollmentRoute = "/api/internal_api/atlas/v1/current_user/role_enrollments/:roleId";

// roleId may arrive as the string id from transformPathData; the API accepts both
export const doUserEnrollment = async (roleId: number | string, abandonOtherPaths?: boolean) => {
  const route = UserEnrollmentRoute.replace("/:roleId", "");
  const client = getRailsClient();
  return client
    .post<EnrollmentResponse>(route, { id: roleId, abandonOtherPaths })
    .then(async response => {
      if (response.status === 200) {
        return response.data;
      } else {
        return Promise.reject(
          new Error(`doUserEnrollment: Unable to enroll user, status code ${response.status}`)
        );
      }
    });
};

export const doUserUnenrollment = async (roleId: number) => {
  const route = UserEnrollmentRoute.replace(":roleId", roleId.toString());
  const client = getRailsClient();
  return client
    .put<EnrollmentMutationResponse>(route, { operation: "unenroll" })
    .then(async response => {
      if (response.status === 200) {
        return response.data;
      } else {
        return Promise.reject(
          new Error(`doUserUnenrollment: Unable to unenroll user, status code ${response.status}`)
        );
      }
    });
};
